/**
 * Design explorer route — /designs
 *
 * Public route (no auth required) that renders design treatments
 * with a compact version/screen selector. On desktop, constrains
 * to mobile width and centers on a dark background.
 *
 * @since 1.0.0
 */
import { useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { createFileRoute } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import type { ComponentType } from "react";
import { Anthem } from "~/components/designs/Anthem.tsx";
import { Aperture } from "~/components/designs/Aperture.tsx";
import { Atlas } from "~/components/designs/Atlas.tsx";
import { Bare } from "~/components/designs/Bare.tsx";
import { Bleedout } from "~/components/designs/Bleedout.tsx";
import { Broadcast } from "~/components/designs/Broadcast.tsx";
import { Carbon } from "~/components/designs/Carbon.tsx";
import { Chronicle } from "~/components/designs/Chronicle.tsx";
import { Clarity } from "~/components/designs/Clarity.tsx";
import { Clearance } from "~/components/designs/Clearance.tsx";
import { Collapse } from "~/components/designs/Collapse.tsx";
import { CollapseFm } from "~/components/designs/CollapseFm.tsx";
import { CollapseFmLight } from "~/components/designs/CollapseFmLight.tsx";
import { CollapseLight } from "~/components/designs/CollapseLight.tsx";
import { Contrast } from "~/components/designs/Contrast.tsx";
import { Crossfade } from "~/components/designs/Crossfade.tsx";
import { Darkroom } from "~/components/designs/Darkroom.tsx";
import { Decode } from "~/components/designs/Decode.tsx";
import { Diegetic } from "~/components/designs/Diegetic.tsx";
import { Direct } from "~/components/designs/Direct.tsx";
import { Dissolve } from "~/components/designs/Dissolve.tsx";
import { Ember } from "~/components/designs/Ember.tsx";
import { Emergence } from "~/components/designs/Emergence.tsx";
import { Evidence } from "~/components/designs/Evidence.tsx";
import { Fieldwork } from "~/components/designs/Fieldwork.tsx";
import { Flatline } from "~/components/designs/Flatline.tsx";
import { Fractal } from "~/components/designs/Fractal.tsx";
import { Frequency } from "~/components/designs/Frequency.tsx";
import { FtuePromise } from "~/components/designs/FtuePromise.tsx";
import { FtuePromiseLight } from "~/components/designs/FtuePromiseLight.tsx";
import { FtuePromiseV2 } from "~/components/designs/FtuePromiseV2.tsx";
import { FtuePromiseV2Light } from "~/components/designs/FtuePromiseV2Light.tsx";
import { FtuePromiseV3 } from "~/components/designs/FtuePromiseV3.tsx";
import { FtuePromiseV3Light } from "~/components/designs/FtuePromiseV3Light.tsx";
import { FtueSituation } from "~/components/designs/FtueSituation.tsx";
import { FtueSituationLight } from "~/components/designs/FtueSituationLight.tsx";
import { FtueSituationV2 } from "~/components/designs/FtueSituationV2.tsx";
import { FtueSituationV2Light } from "~/components/designs/FtueSituationV2Light.tsx";
import { FtueSituationV3 } from "~/components/designs/FtueSituationV3.tsx";
import { FtueSituationV3Light } from "~/components/designs/FtueSituationV3Light.tsx";
import { FtueSplash } from "~/components/designs/FtueSplash.tsx";
import { FtueSplashLight } from "~/components/designs/FtueSplashLight.tsx";
import { FtueSplashV2 } from "~/components/designs/FtueSplashV2.tsx";
import { FtueSplashV2Light } from "~/components/designs/FtueSplashV2Light.tsx";
import { FtueSplashV3 } from "~/components/designs/FtueSplashV3.tsx";
import { FtueSplashV3Light } from "~/components/designs/FtueSplashV3Light.tsx";
import { FtueTrial } from "~/components/designs/FtueTrial.tsx";
import { FtueTrialLight } from "~/components/designs/FtueTrialLight.tsx";
import { FtueTrialV2 } from "~/components/designs/FtueTrialV2.tsx";
import { FtueTrialV2Light } from "~/components/designs/FtueTrialV2Light.tsx";
import { FtueWho } from "~/components/designs/FtueWho.tsx";
import { FtueWhoLight } from "~/components/designs/FtueWhoLight.tsx";
import { FtueWhoV2 } from "~/components/designs/FtueWhoV2.tsx";
import { FtueWhoV2Light } from "~/components/designs/FtueWhoV2Light.tsx";
import { FtueWhoV3 } from "~/components/designs/FtueWhoV3.tsx";
import { FtueWhoV3Light } from "~/components/designs/FtueWhoV3Light.tsx";
import { Glitch } from "~/components/designs/Glitch.tsx";
import { Halflife } from "~/components/designs/Halflife.tsx";
import { Ignite } from "~/components/designs/Ignite.tsx";
import { IgniteFm } from "~/components/designs/IgniteFm.tsx";
import { IgniteFmLight } from "~/components/designs/IgniteFmLight.tsx";
import { IgniteLight } from "~/components/designs/IgniteLight.tsx";
import { Incision } from "~/components/designs/Incision.tsx";
import { IncisionFm } from "~/components/designs/IncisionFm.tsx";
import { IncisionFmLight } from "~/components/designs/IncisionFmLight.tsx";
import { IncisionLight } from "~/components/designs/IncisionLight.tsx";
import { Intercept } from "~/components/designs/Intercept.tsx";
import { Lantern } from "~/components/designs/Lantern.tsx";
import { LiveWire } from "~/components/designs/LiveWire.tsx";
import { Mirror } from "~/components/designs/Mirror.tsx";
import { Mono } from "~/components/designs/Mono.tsx";
import { Overprint } from "~/components/designs/Overprint.tsx";
import { Override } from "~/components/designs/Override.tsx";
import { Playback } from "~/components/designs/Playback.tsx";
import { Polarity } from "~/components/designs/Polarity.tsx";
import { Proof } from "~/components/designs/Proof.tsx";
import { Pulse } from "~/components/designs/Pulse.tsx";
import { Radar } from "~/components/designs/Radar.tsx";
import { Redline } from "~/components/designs/Redline.tsx";
import { Residue } from "~/components/designs/Residue.tsx";
import { Scatter } from "~/components/designs/Scatter.tsx";
import { Shatter } from "~/components/designs/Shatter.tsx";
import { ShatterFm } from "~/components/designs/ShatterFm.tsx";
import { ShatterFmLight } from "~/components/designs/ShatterFmLight.tsx";
import { ShatterLight } from "~/components/designs/ShatterLight.tsx";
import { Signal } from "~/components/designs/Signal.tsx";
import { Signal2 } from "~/components/designs/Signal2.tsx";
import { Signal3 } from "~/components/designs/Signal3.tsx";
import { Signal4 } from "~/components/designs/Signal4.tsx";
import { Signal5 } from "~/components/designs/Signal5.tsx";
import { Signal6 } from "~/components/designs/Signal6.tsx";
import { Split } from "~/components/designs/Split.tsx";
import { Stark } from "~/components/designs/Stark.tsx";
import { Terminal } from "~/components/designs/Terminal.tsx";
import { Thread } from "~/components/designs/Thread.tsx";
import { Threshold } from "~/components/designs/Threshold.tsx";
import { Tidal } from "~/components/designs/Tidal.tsx";
import { Tuning } from "~/components/designs/Tuning.tsx";
import { TuningFm } from "~/components/designs/TuningFm.tsx";
import { TuningFmLight } from "~/components/designs/TuningFmLight.tsx";
import { TuningLight } from "~/components/designs/TuningLight.tsx";
import { Voltage } from "~/components/designs/Voltage.tsx";
import { Wire } from "~/components/designs/Wire.tsx";
import {
  activeScreenAtom,
  activeScreenTypeAtom,
  activeVersionAtom,
  colorModeAtom,
  screenTypeDropdownOpenAtom,
  versionDropdownOpenAtom,
} from "~/lib/atoms/design-explorer.ts";

/* ─── Registry ───────────────────────────────────────────────── */

interface DesignScreen {
  label: string;
  name: string;
  component: ComponentType;
  darkComponent?: ComponentType;
  lightComponent?: ComponentType;
}

interface DesignVersion {
  label: string;
  screens: Array<DesignScreen>;
}

interface ScreenType {
  label: string;
  versions: Array<DesignVersion>;
}

type ColorMode = "system" | "light" | "dark";

function versionHasModes(version: DesignVersion): boolean {
  return version.screens.some((s) => s.darkComponent && s.lightComponent);
}

function resolveComponent(screen: DesignScreen, mode: ColorMode): ComponentType {
  if (!screen.darkComponent && !screen.lightComponent) return screen.component;
  const effective = mode === "system"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light"
    : mode;
  if (effective === "dark" && screen.darkComponent) return screen.darkComponent;
  if (effective === "light" && screen.lightComponent) return screen.lightComponent;
  return screen.component;
}

const SCREEN_TYPES: Array<ScreenType> = [
  {
    label: "Landing",
    versions: [
      {
        label: "v1",
        screens: [
          { label: "I", name: "Darkroom", component: Darkroom },
          { label: "II", name: "Wire", component: Wire },
          { label: "III", name: "Carbon", component: Carbon },
          { label: "IV", name: "Pulse", component: Pulse },
          { label: "V", name: "Mono", component: Mono },
        ],
      },
      {
        label: "v2",
        screens: [
          { label: "I", name: "Fractal", component: Fractal },
          { label: "II", name: "Polarity", component: Polarity },
          { label: "III", name: "Tidal", component: Tidal },
          { label: "IV", name: "Threshold", component: Threshold },
          { label: "V", name: "Ember", component: Ember },
        ],
      },
      {
        label: "v3",
        screens: [
          { label: "I", name: "Glitch", component: Glitch },
          { label: "II", name: "LiveWire", component: LiveWire },
          { label: "III", name: "Scatter", component: Scatter },
          { label: "IV", name: "Signal", component: Signal },
          { label: "V", name: "Voltage", component: Voltage },
        ],
      },
      {
        label: "v4",
        screens: [
          { label: "I", name: "Broadcast", component: Broadcast },
          { label: "II", name: "Decode", component: Decode },
          { label: "III", name: "Clearance", component: Clearance },
          { label: "IV", name: "Frequency", component: Frequency },
          { label: "V", name: "Emergence", component: Emergence },
        ],
      },
      {
        label: "v5",
        screens: [
          { label: "I", name: "Signal II", component: Signal2 },
          { label: "II", name: "Signal III", component: Signal3 },
          { label: "III", name: "Signal IV", component: Signal4 },
          { label: "IV", name: "Signal V", component: Signal5 },
          { label: "V", name: "Signal VI", component: Signal6 },
        ],
      },
      {
        label: "v6",
        screens: [
          { label: "I", name: "Intercept", component: Intercept },
          { label: "II", name: "Playback", component: Playback },
          { label: "III", name: "Terminal", component: Terminal },
          { label: "IV", name: "Radar", component: Radar },
          { label: "V", name: "Diegetic", component: Diegetic },
        ],
      },
      {
        label: "v7",
        screens: [
          { label: "I", name: "Chronicle", component: Chronicle },
          { label: "II", name: "Mirror", component: Mirror },
          { label: "III", name: "Atlas", component: Atlas },
          { label: "IV", name: "Lantern", component: Lantern },
          { label: "V", name: "Thread", component: Thread },
        ],
      },
      {
        label: "v8",
        screens: [
          { label: "I", name: "Anthem", component: Anthem },
          { label: "II", name: "Proof", component: Proof },
          { label: "III", name: "Fieldwork", component: Fieldwork },
          { label: "IV", name: "Contrast", component: Contrast },
          { label: "V", name: "Direct", component: Direct },
        ],
      },
      {
        label: "v9",
        screens: [
          { label: "I", name: "Stark", component: Stark },
          { label: "II", name: "Evidence", component: Evidence },
          { label: "III", name: "Clarity", component: Clarity },
          { label: "IV", name: "Split", component: Split },
          { label: "V", name: "Bare", component: Bare },
        ],
      },
      {
        label: "v10",
        screens: [
          { label: "I", name: "Dissolve", component: Dissolve },
          { label: "II", name: "Redline", component: Redline },
          { label: "III", name: "Halflife", component: Halflife },
          { label: "IV", name: "Residue", component: Residue },
          { label: "V", name: "Override", component: Override },
        ],
      },
      {
        label: "v11",
        screens: [
          { label: "I", name: "Aperture", component: Aperture },
          { label: "II", name: "Flatline", component: Flatline },
          { label: "III", name: "Overprint", component: Overprint },
          { label: "IV", name: "Bleedout", component: Bleedout },
          { label: "V", name: "Crossfade", component: Crossfade },
        ],
      },
      {
        label: "v12",
        screens: [
          { label: "I", name: "Ignite", component: Ignite },
          { label: "II", name: "Tuning", component: Tuning },
          { label: "III", name: "Incision", component: Incision },
          { label: "IV", name: "Collapse", component: Collapse },
          { label: "V", name: "Shatter", component: Shatter },
        ],
      },
      {
        label: "v13",
        screens: [
          { label: "I", name: "Ignite", component: Ignite, darkComponent: Ignite, lightComponent: IgniteLight },
          { label: "II", name: "Tuning", component: Tuning, darkComponent: Tuning, lightComponent: TuningLight },
          {
            label: "III",
            name: "Incision",
            component: Incision,
            darkComponent: Incision,
            lightComponent: IncisionLight,
          },
          {
            label: "IV",
            name: "Collapse",
            component: Collapse,
            darkComponent: Collapse,
            lightComponent: CollapseLight,
          },
          { label: "V", name: "Shatter", component: Shatter, darkComponent: Shatter, lightComponent: ShatterLight },
        ],
      },
      {
        label: "v14",
        screens: [
          { label: "I", name: "Ignite", component: IgniteFm, darkComponent: IgniteFm, lightComponent: IgniteFmLight },
          { label: "II", name: "Tuning", component: TuningFm, darkComponent: TuningFm, lightComponent: TuningFmLight },
          {
            label: "III",
            name: "Incision",
            component: IncisionFm,
            darkComponent: IncisionFm,
            lightComponent: IncisionFmLight,
          },
          {
            label: "IV",
            name: "Collapse",
            component: CollapseFm,
            darkComponent: CollapseFm,
            lightComponent: CollapseFmLight,
          },
          {
            label: "V",
            name: "Shatter",
            component: ShatterFm,
            darkComponent: ShatterFm,
            lightComponent: ShatterFmLight,
          },
        ],
      },
    ],
  },
  {
    label: "FTUE #1",
    versions: [
      {
        label: "v1",
        screens: [
          {
            label: "1",
            name: "Splash",
            component: FtueSplash,
            darkComponent: FtueSplash,
            lightComponent: FtueSplashLight,
          },
          {
            label: "2",
            name: "Promise",
            component: FtuePromise,
            darkComponent: FtuePromise,
            lightComponent: FtuePromiseLight,
          },
          { label: "3", name: "Who", component: FtueWho, darkComponent: FtueWho, lightComponent: FtueWhoLight },
          {
            label: "4",
            name: "Situation",
            component: FtueSituation,
            darkComponent: FtueSituation,
            lightComponent: FtueSituationLight,
          },
          { label: "5", name: "Trial", component: FtueTrial, darkComponent: FtueTrial, lightComponent: FtueTrialLight },
        ],
      },
      {
        label: "v2",
        screens: [
          {
            label: "1",
            name: "Splash",
            component: FtueSplashV2,
            darkComponent: FtueSplashV2,
            lightComponent: FtueSplashV2Light,
          },
          {
            label: "2",
            name: "Promise",
            component: FtuePromiseV2,
            darkComponent: FtuePromiseV2,
            lightComponent: FtuePromiseV2Light,
          },
          { label: "3", name: "Who", component: FtueWhoV2, darkComponent: FtueWhoV2, lightComponent: FtueWhoV2Light },
          {
            label: "4",
            name: "Situation",
            component: FtueSituationV2,
            darkComponent: FtueSituationV2,
            lightComponent: FtueSituationV2Light,
          },
          {
            label: "5",
            name: "Trial",
            component: FtueTrialV2,
            darkComponent: FtueTrialV2,
            lightComponent: FtueTrialV2Light,
          },
        ],
      },
      {
        label: "v3",
        screens: [
          {
            label: "1",
            name: "Splash",
            component: FtueSplashV3,
            darkComponent: FtueSplashV3,
            lightComponent: FtueSplashV3Light,
          },
          {
            label: "2",
            name: "Promise",
            component: FtuePromiseV3,
            darkComponent: FtuePromiseV3,
            lightComponent: FtuePromiseV3Light,
          },
          { label: "3", name: "Who", component: FtueWhoV3, darkComponent: FtueWhoV3, lightComponent: FtueWhoV3Light },
          {
            label: "4",
            name: "Situation",
            component: FtueSituationV3,
            darkComponent: FtueSituationV3,
            lightComponent: FtueSituationV3Light,
          },
          {
            label: "5",
            name: "Trial",
            component: FtueTrialV2,
            darkComponent: FtueTrialV2,
            lightComponent: FtueTrialV2Light,
          },
        ],
      },
    ],
  },
  { label: "Login", versions: [] },
  { label: "Sign In", versions: [] },
  { label: "Flirt Scenario", versions: [] },
];

/* ─── Route ──────────────────────────────────────────────────── */

export const Route = createFileRoute("/designs")({
  head: () => ({
    meta: [{ title: "FlirtMachine — Design Explorer" }],
    links: [
      {
        rel: "preconnect",
        href: "https://fonts.googleapis.com",
      },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous" as const,
      },
      {
        rel: "stylesheet",
        href:
          "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=Syne:wght@400;700;800&family=Barlow+Condensed:wght@400;600;700&family=JetBrains+Mono:wght@400;500;700&family=Outfit:wght@300;400;600;700&family=IBM+Plex+Mono:wght@300;400;500&family=Instrument+Serif&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=Space+Mono:wght@400;700&family=Manrope:wght@300;400;500;600;700&family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Libre+Franklin:wght@300;400;500;600;700&family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap",
      },
    ],
  }),
  component: DesignsPage,
});

/* ─── Page ───────────────────────────────────────────────────── */

function DesignsPage() {
  const activeScreenType = useAtomValue(activeScreenTypeAtom);
  const activeVersion = useAtomValue(activeVersionAtom);
  const activeScreen = useAtomValue(activeScreenAtom);
  const colorMode = useAtomValue(colorModeAtom);

  const screenType = SCREEN_TYPES[activeScreenType];
  const hasVersions = screenType?.versions.length > 0;
  const version = hasVersions ? screenType.versions[activeVersion] : undefined;
  const screen = version?.screens[activeScreen];
  const ScreenComponent = screen ? resolveComponent(screen, colorMode) : undefined;

  return (
    <div className="min-h-screen bg-[#111] flex flex-col items-center justify-start">
      {/* Phone viewport container — centered on desktop */}
      <div
        className="relative w-full overflow-hidden"
        style={{ maxWidth: 430, minHeight: "100dvh" }}
      >
        <AnimatePresence mode="wait">
          {ScreenComponent ?
            (
              <motion.div
                key={`${activeScreenType}-${activeVersion}-${activeScreen}-${colorMode}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="min-h-screen"
              >
                <ScreenComponent />
              </motion.div>
            ) :
            (
              <motion.div
                key={`empty-${activeScreenType}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="min-h-screen flex items-center justify-center"
              >
                <span
                  style={{
                    fontSize: 11,
                    letterSpacing: "3px",
                    color: "rgba(255,255,255,0.2)",
                    textTransform: "uppercase",
                  }}
                >
                  no designs yet
                </span>
              </motion.div>
            )}
        </AnimatePresence>
      </div>

      {/* Version/Screen selector — fixed bottom bar */}
      <VersionSelector />
    </div>
  );
}

/* ─── Selector ───────────────────────────────────────────────── */

function VersionSelector() {
  const activeScreenType = useAtomValue(activeScreenTypeAtom);
  const activeVersion = useAtomValue(activeVersionAtom);
  const activeScreen = useAtomValue(activeScreenAtom);
  const setScreenType = useAtomSet(activeScreenTypeAtom);
  const setVersion = useAtomSet(activeVersionAtom);
  const setScreen = useAtomSet(activeScreenAtom);
  const isScreenTypeDropdownOpen = useAtomValue(screenTypeDropdownOpenAtom);
  const setScreenTypeDropdownOpen = useAtomSet(screenTypeDropdownOpenAtom);
  const isVersionDropdownOpen = useAtomValue(versionDropdownOpenAtom);
  const setVersionDropdownOpen = useAtomSet(versionDropdownOpenAtom);
  const colorMode = useAtomValue(colorModeAtom);
  const setColorMode = useAtomSet(colorModeAtom);

  const screenType = SCREEN_TYPES[activeScreenType];
  const hasVersions = screenType?.versions.length > 0;
  const versions = screenType?.versions ?? [];
  const version = hasVersions ? versions[activeVersion] : undefined;
  const screenLabels = version?.screens ?? [];
  const screenName = version?.screens[activeScreen]?.name ?? "";
  const showModeToggle = version ? versionHasModes(version) : false;

  const isAnyDropdownOpen = isScreenTypeDropdownOpen || isVersionDropdownOpen;

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-50"
      style={{
        bottom: "max(12px, env(safe-area-inset-bottom, 12px))",
        width: "min(calc(100% - 24px), 406px)",
      }}
    >
      {/* Backdrop to close dropdowns on outside click */}
      {isAnyDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setScreenTypeDropdownOpen(false);
            setVersionDropdownOpen(false);
          }}
        />
      )}

      {/* Screen type dropdown list — opens upward */}
      {isScreenTypeDropdownOpen && (
        <div
          className="absolute bottom-full mb-2 left-0 z-50 rounded-xl overflow-hidden"
          style={{
            backgroundColor: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(20px) saturate(1.5)",
            WebkitBackdropFilter: "blur(20px) saturate(1.5)",
            border: "1px solid rgba(255,255,255,0.08)",
            minWidth: 160,
          }}
        >
          <div className="py-1.5">
            {SCREEN_TYPES.map((st, i) => (
              <button
                key={st.label}
                onClick={() => {
                  setScreenType(i);
                  setVersion(0);
                  setScreen(0);
                  setScreenTypeDropdownOpen(false);
                }}
                className="w-full text-left px-3.5 py-2 flex items-center gap-3 transition-colors"
                style={{
                  backgroundColor: i === activeScreenType
                    ? "rgba(255,255,255,0.08)"
                    : "transparent",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: i === activeScreenType ? 700 : 400,
                    letterSpacing: "1px",
                    color: i === activeScreenType
                      ? "white"
                      : "rgba(255,255,255,0.4)",
                  }}
                >
                  {st.label}
                </span>
                {st.versions.length > 0 && (
                  <span
                    style={{
                      fontSize: 9,
                      color: "rgba(255,255,255,0.15)",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {st.versions.length} version{st.versions.length !== 1 ? "s" : ""}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Version dropdown list — opens upward */}
      {isVersionDropdownOpen && hasVersions && (
        <div
          className="absolute bottom-full mb-2 left-0 z-50 rounded-xl overflow-hidden"
          style={{
            backgroundColor: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(20px) saturate(1.5)",
            WebkitBackdropFilter: "blur(20px) saturate(1.5)",
            border: "1px solid rgba(255,255,255,0.08)",
            minWidth: 160,
          }}
        >
          <div className="py-1.5">
            {versions.map((v, i) => (
              <button
                key={v.label}
                onClick={() => {
                  setVersion(i);
                  setScreen(0);
                  setVersionDropdownOpen(false);
                }}
                className="w-full text-left px-3.5 py-2 flex items-center gap-3 transition-colors"
                style={{
                  backgroundColor: i === activeVersion
                    ? "rgba(255,255,255,0.08)"
                    : "transparent",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: i === activeVersion ? 700 : 400,
                    letterSpacing: "1px",
                    color: i === activeVersion
                      ? "white"
                      : "rgba(255,255,255,0.4)",
                  }}
                >
                  {v.label}
                </span>
                <span
                  style={{
                    fontSize: 9,
                    color: "rgba(255,255,255,0.15)",
                    letterSpacing: "0.5px",
                  }}
                >
                  {v.screens.map((s) => s.name).join(", ")}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: "rgba(0,0,0,0.75)",
          backdropFilter: "blur(20px) saturate(1.5)",
          WebkitBackdropFilter: "blur(20px) saturate(1.5)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex items-center px-2.5 py-2.5 gap-2">
          {/* Screen type dropdown trigger */}
          <button
            onClick={() => {
              setVersionDropdownOpen(false);
              setScreenTypeDropdownOpen(!isScreenTypeDropdownOpen);
            }}
            className="flex items-center gap-1.5 shrink-0 transition-all"
            style={{
              padding: "5px 10px",
              borderRadius: 8,
              backgroundColor: isScreenTypeDropdownOpen
                ? "rgba(255,255,255,0.12)"
                : "rgba(255,255,255,0.06)",
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "1px",
                color: "white",
              }}
            >
              {screenType?.label}
            </span>
            <span
              style={{
                fontSize: 8,
                color: "rgba(255,255,255,0.3)",
                transform: isScreenTypeDropdownOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.15s",
                display: "inline-block",
              }}
            >
              ▲
            </span>
          </button>

          {/* Version dropdown trigger + Screen pills — only when versions exist */}
          {hasVersions && (
            <>
              <button
                onClick={() => {
                  setScreenTypeDropdownOpen(false);
                  setVersionDropdownOpen(!isVersionDropdownOpen);
                }}
                className="flex items-center gap-1.5 pr-2.5 border-r border-white/10 shrink-0 transition-all"
                style={{
                  padding: "5px 10px",
                  borderRadius: 8,
                  backgroundColor: isVersionDropdownOpen
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(255,255,255,0.06)",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "1px",
                    color: "white",
                  }}
                >
                  {version?.label}
                </span>
                <span
                  style={{
                    fontSize: 8,
                    color: "rgba(255,255,255,0.3)",
                    transform: isVersionDropdownOpen ? "rotate(180deg)" : "none",
                    transition: "transform 0.15s",
                    display: "inline-block",
                  }}
                >
                  ▲
                </span>
              </button>

              {/* Mode toggle — only when version has light/dark variants */}
              {showModeToggle && (
                <div
                  className="flex items-center shrink-0 pr-2.5 border-r border-white/10"
                  style={{ borderRadius: 8 }}
                >
                  {(
                    [
                      { mode: "system" as ColorMode, icon: "\u2699" },
                      { mode: "light" as ColorMode, icon: "\u2600" },
                      { mode: "dark" as ColorMode, icon: "\u263E" },
                    ] as const
                  ).map(({ icon, mode }) => (
                    <button
                      key={mode}
                      onClick={() => setColorMode(mode)}
                      className="transition-all"
                      style={{
                        padding: "4px 7px",
                        borderRadius: 6,
                        backgroundColor: colorMode === mode
                          ? "rgba(255,255,255,0.12)"
                          : "transparent",
                        fontSize: 13,
                        lineHeight: 1,
                        color: colorMode === mode
                          ? "white"
                          : "rgba(255,255,255,0.3)",
                      }}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              )}

              {/* Screen pills */}
              <div className="flex items-center flex-1 justify-around">
                {screenLabels.map((s, i) => (
                  <button
                    key={s.label}
                    onClick={() => setScreen(i)}
                    className="transition-all"
                    style={{
                      padding: "5px 10px",
                      borderRadius: 8,
                      backgroundColor: i === activeScreen ? "white" : "transparent",
                      color: i === activeScreen ? "black" : "rgba(255,255,255,0.35)",
                      fontSize: 11,
                      fontWeight: i === activeScreen ? 800 : 500,
                      letterSpacing: "2px",
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Current design name — subtle label */}
        {screenName && (
          <div
            className="text-center pb-2 -mt-0.5"
            style={{
              fontSize: 9,
              letterSpacing: "3px",
              color: "rgba(255,255,255,0.15)",
            }}
          >
            {screenName.toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
}
